'use client';

import { ChevronLeft, ChevronRight } from 'react-feather';

export const BLOG_PAGE_SIZE_OPTIONS = [6, 9, 12] as const;
export type BlogPageSize = (typeof BLOG_PAGE_SIZE_OPTIONS)[number];
export const DEFAULT_BLOG_PAGE_SIZE: BlogPageSize = 9;

type BlogListPaginationProps = {
  currentPage: number;
  totalPages: number;
  perPage: BlogPageSize;
  totalFilteredPosts: number;
  onPageChange: (pageNumber: number) => void;
  onPerPageChange: (pageSize: BlogPageSize) => void;
};

function buildPageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pageNumbers = new Set<number>([1, totalPages, currentPage]);
  for (const offset of [-1, 1]) {
    const neighborPage = currentPage + offset;
    if (neighborPage > 1 && neighborPage < totalPages) {
      pageNumbers.add(neighborPage);
    }
  }

  return [...pageNumbers].sort((leftPage, rightPage) => leftPage - rightPage);
}

export function BlogListPagination({
  currentPage,
  totalPages,
  perPage,
  totalFilteredPosts,
  onPageChange,
  onPerPageChange,
}: BlogListPaginationProps) {
  if (totalFilteredPosts === 0) {
    return null;
  }

  const pageNumbers = buildPageNumbers(currentPage, totalPages);
  const rangeStart = (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalFilteredPosts);

  const paginationControlClassName =
    'inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-[color,background-color,border-color,opacity] duration-200 ease-out hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-700 motion-reduce:transition-none';

  return (
    <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
        <label className="inline-flex items-center gap-2">
          <span className="font-medium text-slate-700">Show</span>
          <select
            value={perPage}
            onChange={(event) => {
              const nextPageSize = Number(event.target.value);
              if (
                BLOG_PAGE_SIZE_OPTIONS.includes(
                  nextPageSize as BlogPageSize,
                )
              ) {
                onPerPageChange(nextPageSize as BlogPageSize);
              }
            }}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 shadow-sm outline-none transition-[color,border-color,box-shadow] duration-200 ease-out focus:border-primary focus:ring-2 focus:ring-primary/20 motion-reduce:transition-none"
            aria-label="Blog posts per page"
          >
            {BLOG_PAGE_SIZE_OPTIONS.map((pageSizeOption) => (
              <option key={pageSizeOption} value={pageSizeOption}>
                {pageSizeOption}
              </option>
            ))}
          </select>
        </label>
        <span aria-live="polite">
          {rangeStart}–{rangeEnd} of {totalFilteredPosts}
        </span>
      </div>

      {totalPages > 1 ? (
        <nav
          className="flex flex-wrap items-center gap-1"
          aria-label="Blog pagination"
        >
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className={paginationControlClassName}
          >
            <ChevronLeft className="size-4" />
            Previous
          </button>

          {pageNumbers.map((pageNumber, pageIndex) => {
            const previousPageNumber = pageNumbers[pageIndex - 1];
            const showEllipsis =
              previousPageNumber != null &&
              pageNumber - previousPageNumber > 1;
            const isActivePage = pageNumber === currentPage;

            return (
              <span key={pageNumber} className="contents">
                {showEllipsis ? (
                  <span className="px-1 text-sm text-slate-400" aria-hidden>
                    …
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  aria-current={isActivePage ? 'page' : undefined}
                  className={
                    isActivePage
                      ? 'inline-flex min-w-9 items-center justify-center rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground transition-[color,background-color,border-color] duration-200 ease-out motion-reduce:transition-none'
                      : 'inline-flex min-w-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-[color,background-color,border-color] duration-200 ease-out hover:border-primary hover:text-primary motion-reduce:transition-none'
                  }
                >
                  {pageNumber}
                </button>
              </span>
            );
          })}

          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className={paginationControlClassName}
          >
            Next
            <ChevronRight className="size-4" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}
